import React from 'react'
import Pt from 'prop-types'
import Ipt from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import { Alert, Button, Col, PageHeader, Panel, Row } from 'react-bootstrap'

import Icon from 'internal-components/src/components/Icon'
import { SignupWithExternalsRecord } from 'internal-components/src/records'
import {
  selectClientsBillingGroups,
  selectClients,
} from 'internal-components/src/redux/modules/client'
import { selectAuthenticatedClientIds } from 'internal-components/src/redux/modules/authentication'

import AccountDetails from '../../components/Signup/AccountDetails'
import ClientSelector from '../../components/Signup/ClientSelector'
import CompanyDetails from '../../components/Signup/CompanyDetails'
import PasswordSecurity from '../../components/Signup/PasswordSecurity'
import ProgressSummary from '../../components/Signup/ProgressSummary'
import * as signupActions from '../../redux/modules/signupEdit/actions'
import createSignupProgress, {
  SignupProgressRecord,
} from '../../records/SignupProgress'
import {
  selectErrorsByPath,
  selectEditSignupWithExternals,
} from '../../redux/modules/signupEdit/selectors'

export function AccountView({
  advanceToDetail,
  authenticate,
  billingGroups,
  emailChecking,
  emailRegistered,
  errors,
  revisiting,
  selectClient,
  setAbn,
  setChallengePhrase,
  setCompanyName,
  setCompanySize,
  setContact,
  setDateOfBirth,
  setEmail,
  setEmailConfirm,
  setFirstName,
  setLastName,
  setUserPassword,
  signupProgress,
  signupWithExternals,
  user,
  working,
}) {
  const handleSubmit = e => {
    e.preventDefault()

    advanceToDetail()
  }

  const signup = signupWithExternals.signup
  const formClass = revisiting ? 'revisiting' : ''

  return (
    <section className="container">
      <PageHeader>
        {signup.reference && (
          <small className="pull-right">{signup.reference}</small>
        )}
        Personal and Business Account Details
      </PageHeader>

      <ProgressSummary
        signupProgress={signupProgress}
        signupWithExternals={signupWithExternals}
      />

      <form
        onSubmit={handleSubmit}
        className={formClass}
        disabled={emailRegistered}
        noValidate
      >
        {revisiting && (
          <div className="notProvided">
            <Panel>
              <Panel.Body>
                <h3>
                  <Icon name="exclamation-triangle" /> Partially complete Signup
                </h3>

                <p>
                  This signup is partially complete and requires additional
                  information before it can be submitted.
                </p>

                <p>
                  Any required fields that are not filled out are identified by
                  an orange line to the left of the field.
                </p>
              </Panel.Body>
            </Panel>
          </div>
        )}

        {!user && (
          <div>
            <h2>Personal Details</h2>

            <Row>
              <Col md={4} mdPush={8}>
                <Panel>
                  <Panel.Body>
                    <p>
                      <strong>
                        When you contact us for support or to make changes to
                        your account we will verify you with the information you
                        provide here.
                      </strong>
                    </p>

                    <p>
                      You can add more authorised individuals to your account by
                      contacting us by emailing{' '}
                      <a href="mailto:support@infinite.net.au">
                        support@infinite.net.au
                      </a>{' '}
                      or calling us during business hours on{' '}
                      <tel>1300 790 337</tel>.
                    </p>
                  </Panel.Body>
                </Panel>
              </Col>

              <Col md={8} mdPull={4}>
                <AccountDetails
                  authenticate={authenticate}
                  emailChecking={emailChecking}
                  emailRegistered={emailRegistered}
                  errors={errors}
                  setChallengePhrase={setChallengePhrase}
                  setContact={setContact}
                  setDateOfBirth={setDateOfBirth}
                  setEmail={setEmail}
                  setEmailConfirm={setEmailConfirm}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                  setUserPassword={setUserPassword}
                  signup={signup}
                />
              </Col>
            </Row>
          </div>
        )}

        {billingGroups.size > 0 && (
          <div>
            <h3>Client Selection</h3>

            <Row>
              <Col md={4} mdPush={8}>
                <Panel>
                  <Panel.Body>
                    <p>
                      On this screen you can manage which account will be used
                      for the creation of your new service.
                    </p>

                    <p>
                      <strong>Creating a new client</strong>
                    </p>

                    <p>
                      When creating a new client account, you will be added as
                      the sole authorised contact and the service will be added
                      under that account. It will have no association with other
                      clients you are a member of.
                    </p>

                    <p>
                      <strong>Using an existing group</strong>
                    </p>

                    <p>
                      Infinite Networks allows you to group services under
                      billing groups which are logical separators to keep
                      different types of services organised. We only ever put
                      services from the same billing group on the same invoice.
                    </p>

                    <p>
                      You are able to put this new service under any existing
                      billing group by selecting the appropriate group to the
                      left.
                    </p>

                    <p>
                      <strong>Creating a new group</strong>
                    </p>

                    <p>
                      You can also create a new group under an existing client
                      that will be used for this service.
                    </p>
                  </Panel.Body>
                </Panel>

                <Panel>
                  <Panel.Body>
                    <p>
                      If you need assistance with billing groups or managing
                      your account, please contact us by emailing{' '}
                      <a href="mailto:support@infinite.net.au">
                        support@infinite.net.au
                      </a>{' '}
                      or calling us during business hours on{' '}
                      <tel>1300 790 337</tel>.
                    </p>
                  </Panel.Body>
                </Panel>
              </Col>

              <Col md={8} mdPull={4}>
                <ClientSelector
                  billingGroups={billingGroups}
                  selectClient={selectClient}
                  selectedClientId={signup.clientId}
                  selectedBillingGroupId={signup.billingGroupId}
                  user={user}
                />
              </Col>
            </Row>
          </div>
        )}

        {!signup.get('billingGroupId') && (
          <div>
            <h3>Business Details</h3>

            <Row>
              <Col md={4} mdPush={8}>
                <Panel>
                  <Panel.Body>
                    <p>
                      If you&apos;re setting up an account for a business, enter
                      the business name, size, and ABN here.
                    </p>

                    <p>
                      If you put details in these fields, the account will be
                      created under the business name.
                    </p>
                  </Panel.Body>
                </Panel>
              </Col>

              <Col md={8} mdPull={4}>
                <CompanyDetails
                  errors={errors}
                  setAbn={setAbn}
                  setCompanyName={setCompanyName}
                  setCompanySize={setCompanySize}
                  signup={signup}
                />
              </Col>
            </Row>
          </div>
        )}

        {errors.has('general') && <div className="attention-contact-us">
          <Panel>
            <Panel.Body>
              <h3>
                <Icon name="exclamation-triangle" /> Please contact us for assistance.
              </h3>
              <p>
                Please email us at <a href="mailto:support@infinite.net.au">support@infinite.net.au</a> or call us during business hours on{' '}
                <tel>1300 790 337</tel>.
              </p>
            </Panel.Body>
          </Panel>
        </div>}

        <Row>
          <Col md={12}>
            {!emailRegistered && (
              <Button bsSize="large" type="submit" disabled={working}>
                Continue&nbsp;
                <Icon name="long-arrow-right" spin={working} />
              </Button>
            )}

            {emailRegistered && (
              <Alert bsStyle="warning" style={{ marginTop: '0.5em' }}>
                <p>
                  <Icon name="exclamation-triangle" /> This email address is
                  already registered with us. To continue with this signup,
                  please log in.
                </p>

                <Button
                  bsSize="sm"
                  bsStyle="warning"
                  onClick={() => authenticate()}
                  style={{ marginTop: '0.5em' }}
                >
                  <Icon name="user" /> Log in
                </Button>
              </Alert>
            )}
          </Col>
        </Row>
      </form>
    </section>
  )
}

AccountView.propTypes = {
  advanceToDetail: Pt.func.isRequired,
  authenticate: Pt.func.isRequired,
  billingGroups: Ipt.orderedMap.isRequired,
  emailChecking: Pt.bool.isRequired,
  emailRegistered: Pt.bool,
  errors: Ipt.map.isRequired,
  revisiting: Pt.bool.isRequired,
  selectClient: Pt.func.isRequired,
  setAbn: Pt.func.isRequired,
  setChallengePhrase: Pt.func.isRequired,
  setCompanyName: Pt.func.isRequired,
  setCompanySize: Pt.func.isRequired,
  setContact: Pt.func.isRequired,
  setDateOfBirth: Pt.func.isRequired,
  setEmail: Pt.func.isRequired,
  setEmailConfirm: Pt.func.isRequired,
  setFirstName: Pt.func.isRequired,
  setLastName: Pt.func.isRequired,
  setUserPassword: Pt.func.isRequired,
  signupProgress: Pt.instanceOf(SignupProgressRecord).isRequired,
  signupWithExternals: Pt.instanceOf(SignupWithExternalsRecord).isRequired,
  user: Ipt.record,
  working: Pt.bool.isRequired,
}

const mapStateToProps = state => {
  const clientIds = selectAuthenticatedClientIds(state.authentication)
  const clients = selectClients(state.client, clientIds)
  const signupWithExternals = selectEditSignupWithExternals(state, {
    sortBy: null,
  })

  return {
    billingGroups: selectClientsBillingGroups(
      clients,
      state.billingGroup.get('groups'),
    ),
    emailChecking: state.signupEdit.getIn(['meta', 'emailChecking']),
    emailRegistered: state.signupEdit.getIn(['meta', 'emailRegistered']),
    errors: selectErrorsByPath(state.signupEdit),
    revisiting: state.signupEdit.getIn(['meta', 'revisiting']),
    signupProgress: createSignupProgress(
      signupWithExternals,
      state.signupEdit.get('errors'),
      'account',
    ),
    signupWithExternals,
    user: state.authentication.get('user'),
    working: state.signupEdit.getIn(['meta', 'working']),
  }
}

export default connect(
  mapStateToProps,
  signupActions,
)(AccountView)



// WEBPACK FOOTER //
// ./src/views/SignupForm/AccountView.jsx